import { API_BASE, DEVICE_CODE_KEY, DEVICE_ID_KEY } from "../lib/constants.js";
// Native Run A: every call to this server goes through apiUrl (src/lib/origin.js) — identity on
// the web, the API host in a native build. scripts/check-native.mjs fails on a bare fetch(apiUrl("/…")).
import { apiUrl } from "./origin.js";

// This device's own persistent identity — created once, the first time anything in this
// module needs it, and reused for the life of this browser install. It's what lets Parent
// Mode → Settings → Devices list this browser/install as its own row, and what lets
// "Remove" kick it out (see worker/lib/devices.js). Exported so DevicesCard can compare a
// row's id against this device's own id (to disable removing yourself).
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private-browsing lockdown, etc.) — fall back to a
    // session-only id so requests still work, just without persistent device tracking.
    return crypto.randomUUID();
  }
}

// Called only when this browser deliberately creates or (re)joins a household (Root.jsx's
// handleStart/handleJoin) — gives it a brand-new identity instead of reusing whatever id it
// had before. This is what lets a previously-kicked device back in: a revoked flag lives
// against the OLD device id forever (see worker/lib/devices.js — entries are kept, not
// deleted, precisely so a revoked id stays rejected), so simply entering the correct code
// and passcode again would otherwise keep hitting that same revoked id and keep failing.
// A fresh id has no revocation history at all, so it just works. An ordinary reload does
// NOT call this — getDeviceId() keeps reusing the same id then, so the device list stays
// stable rather than growing a new row every time someone opens the app.
export function regenerateDeviceId() {
  const id = crypto.randomUUID();
  try { localStorage.setItem(DEVICE_ID_KEY, id); } catch {}
  return id;
}

/* ------------------------- W1.7: offline household cache -------------------------
 * The app shell already loads offline (service worker), but household data never did:
 * a device with no connection got a raw fetch error ("Load failed" on Safari) surfaced
 * onto the onboarding screen — reported by Alan from a plane, Aug 2026. These helpers
 * are the read-through cache Root.jsx falls back to: every successful load AND every
 * successful save writes the household JSON here, so a device that has ever synced can
 * always open to its last-known data. Cleared on leave/revoke — a device removed from a
 * household must not retain a readable copy of it. localStorage (not Cache Storage) on
 * purpose: it's synchronous, survives the SW cache-name bumps the force-refresh fix
 * cycles through, and household JSON is small (images live in R2 as URLs, never inline).
 * All three swallow storage errors — the cache is best-effort, never load-bearing.
 * -------------------------------------------------------------------------------- */
export const HOUSEHOLD_CACHE_PREFIX = "lb-household-cache-";

export function writeCachedHousehold(code, stateObj) {
  try { localStorage.setItem(`${HOUSEHOLD_CACHE_PREFIX}${code}`, JSON.stringify({ savedAt: Date.now(), state: stateObj })); } catch { /* quota/blocked — best-effort */ }
}

export function readCachedHousehold(code) {
  try {
    const raw = localStorage.getItem(`${HOUSEHOLD_CACHE_PREFIX}${code}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.state && typeof parsed.state === "object" ? parsed : null;
  } catch { return null; }
}

export function clearCachedHousehold(code) {
  try { localStorage.removeItem(`${HOUSEHOLD_CACHE_PREFIX}${code}`); } catch {}
}

// A fetch that never reached the server (offline, DNS, aborted) throws a TypeError whose
// message varies by engine — Safari says "Load failed", Chrome "Failed to fetch", Firefox
// "NetworkError when attempting to fetch resource". This is the ONLY class of failure the
// offline cache may answer for: a reachable server saying no (4xx/5xx, revoked, unknown
// code) must keep surfacing exactly as it does today.
export function isNetworkError(err) {
  if (!err) return false;
  if (err instanceof TypeError) return true;
  return /load failed|failed to fetch|networkerror|network request failed|internet|offline/i.test(err.message || "");
}

// W2-PR(ii) — per-device API token, stored per household code. Attached as
// X-Device-Token on household reads/writes; picked up from X-Device-Token-Issue when
// the worker auto-mints during the dual-accept window (see worker/lib/deviceTokens.js).
// localStorage failures degrade to tokenless requests, which the dual-accept window
// tolerates — and under enforcement the 401's tokenRequired flag routes to rejoin.
export const DEVICE_TOKEN_KEY_PREFIX = "lb_device_token_";
export function getDeviceToken(code) {
  try { return localStorage.getItem(DEVICE_TOKEN_KEY_PREFIX + code) || null; } catch { return null; }
}
export function setDeviceToken(code, token) {
  try { localStorage.setItem(DEVICE_TOKEN_KEY_PREFIX + code, token); } catch {}
}
export function clearDeviceToken(code) {
  try { localStorage.removeItem(DEVICE_TOKEN_KEY_PREFIX + code); } catch {}
}

// LB-178 — what this device has proved. Mirrors the worker's record (passcode | jointoken |
// pin | parent | handover = proven; auto | none = not). Refreshed from the X-Device-Proof
// header on every household GET and from the device-token response, so a device reset
// server-side (or a fresh install) re-learns it on first load. Only used to decide whether
// a Parent PIN unlock should also re-mint — see ensureProvenDevice().
// handover is a one-time ticket, issued to an already-joined session and redeemed once
// (LB-336); invite is an adult who accepted an emailed invite on this device (LB-342).
// Keep this set in step with PROVEN_KINDS in worker/lib/deviceTokens.js.
export const DEVICE_PROOF_KEY_PREFIX = "lb_device_proof_";
const PROVEN = new Set(["passcode", "jointoken", "pin", "parent", "handover", "invite"]);
export function getDeviceProof(code) {
  try { return localStorage.getItem(DEVICE_PROOF_KEY_PREFIX + code) || "none"; } catch { return "none"; }
}
export function setDeviceProof(code, proof) {
  try { localStorage.setItem(DEVICE_PROOF_KEY_PREFIX + code, String(proof || "none")); } catch {}
}
export function isProvenProof(proof) { return PROVEN.has(proof); }

// LB-340 — WHOSE device this is. Stored beside the token, from the same three places the
// worker sets it: the device-token mint (a PIN proof names the adult who proved it), a
// successful verify-pin, and Settings -> Devices -> Confirm. null/absent means a SHARED
// family device, which is what a passcode or QR join produces — neither can say who was
// holding the phone — and a shared device asks for the Owner's PIN (screens D1/D2).
//
// This is what lets every PIN call below carry an adultId without threading one through a
// dozen call sites: the device already knows who it belongs to, and the caller only has to
// say otherwise when the parent taps "Log in as" and means it.
export const DEVICE_OWNER_KEY_PREFIX = "lb_device_owner_";
export function getDeviceOwnerAdultId(code) {
  try { return localStorage.getItem(DEVICE_OWNER_KEY_PREFIX + code) || null; } catch { return null; }
}
export function setDeviceOwnerAdultId(code, adultId) {
  try {
    if (adultId) localStorage.setItem(DEVICE_OWNER_KEY_PREFIX + code, String(adultId));
    else localStorage.removeItem(DEVICE_OWNER_KEY_PREFIX + code);
  } catch {}
}

// LB-343 — who unlocked Parent Mode in THIS session, which is who an action is attributed to.
// Distinct from the device's owner above, and deliberately session-scoped: on a shared family
// phone the device belongs to nobody, and "log in as" means the answer changes between one
// Parent Mode session and the next. Closing the tab forgets it, which is the right lifetime
// for "who is holding this phone right now".
//
// Stored rather than passed as a prop for the same reason the device owner is: every write
// site needs it, and threading it through a dozen components is a dozen chances to forget.
// The household this device is on. Read straight from where Root already stores it, so a
// component that never received `code` as a prop can still attribute what it writes.
export function getHouseholdCode() {
  try { return localStorage.getItem(DEVICE_CODE_KEY) || null; } catch { return null; }
}

export const ACTIVE_ADULT_KEY_PREFIX = "lb_active_adult_";
export function getActiveAdultId(code) {
  try { return sessionStorage.getItem(ACTIVE_ADULT_KEY_PREFIX + code) || null; } catch { return null; }
}
export function setActiveAdultId(code, adultId) {
  try {
    if (adultId) sessionStorage.setItem(ACTIVE_ADULT_KEY_PREFIX + code, String(adultId));
    else sessionStorage.removeItem(ACTIVE_ADULT_KEY_PREFIX + code);
  } catch {}
}

// LB-342 (20 Sep) — the Parent Mode grace window.
//
// The install path reloads the page, on purpose: the carried URL is how the icon learns which
// household it belongs to. But a reload is a fresh boot, so Parent Mode re-armed its PIN pad
// and asked again two seconds after the parent had typed it — twice in a row if they then hit
// the next install CTA. It reads as a bug and is not one, which is the worst kind (Alan, 20
// Sep).
//
// sessionStorage is the right shelf for this and no other. It survives a reload of THIS tab,
// which is the case we are forgiving. It dies with the tab, so "the app was closed" needs no
// code. And a Home-Screen launch gets its own storage jar, so an installed icon never inherits
// an unlock from the browser tab it was installed from — the one place where forgiving would
// be wrong. localStorage would have been wrong on all three counts.
//
// Backgrounding is handled by the clock rather than by an event: away for ten seconds and back
// is forgiven, away for ten minutes is not. Re-locking on every visibility change would fail a
// parent who glanced at a notification, which is the same annoyance in a different coat.
export const PARENT_GRACE_MS = 5 * 60 * 1000;
const PARENT_GRACE_KEY_PREFIX = "lb_parent_unlocked_";

export function markParentUnlocked(code) {
  try { sessionStorage.setItem(PARENT_GRACE_KEY_PREFIX + code, String(Date.now())); } catch {}
}

// Called when the parent deliberately switches to Kid Mode. The window forgives a reload this
// tab performed; it must never forgive somebody handing the phone to a child and Parent Mode
// asking for nothing on the way back.
export function clearParentUnlocked(code) {
  try { sessionStorage.removeItem(PARENT_GRACE_KEY_PREFIX + code); } catch {}
}

// When the PIN was last proved in this tab, or null. Distinct from parentUnlockedRecently:
// that asks "is the window open", this asks "was it ever opened and not since surrendered".
// The hide branch in App needs the second question — it EXTENDS an unlock, and must never
// create one for a tab that has not proved anything, nor resurrect one Kid Mode cleared.
export function parentUnlockStampedAt(code) {
  try {
    const raw = sessionStorage.getItem(PARENT_GRACE_KEY_PREFIX + code);
    if (!raw) return null;
    const at = Number(raw);
    return Number.isFinite(at) && at <= Date.now() ? at : null;
  } catch { return null; }
}

// True only while a PIN typed in THIS tab is still inside the window. Anything unreadable,
// unparseable or in the future reads as false: this decides whether to skip a lock, so the
// failure has to be the safe direction.
export function parentUnlockedRecently(code) {
  try {
    const raw = sessionStorage.getItem(PARENT_GRACE_KEY_PREFIX + code);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at) || at > Date.now()) return false;
    return Date.now() - at < PARENT_GRACE_MS;
  } catch { return false; }
}

// The re-challenge. Called by App.jsx right after a Parent PIN is accepted: if this
// device has never proved it belongs (its token came from the dual-accept auto-mint, or it
// has none), the PIN the parent just typed is the proof — re-mint with it. Nothing is
// shown to the parent; the PIN pad they already used IS the challenge. Idempotent and
// cheap: a proven device returns immediately, so this costs nothing after the first
// unlock. Never throws into the unlock path.
export async function ensureProvenDevice(code, pin, adultId) {
  try {
    if (isProvenProof(getDeviceProof(code))) return true;
    if (typeof pin !== "string" || !pin) return false;
    // LB-340: the mint records this adult as the device's owner, so the next unlock asks
    // for their PIN rather than the Owner's.
    return await apiMintDeviceToken(code, { verifiedPin: pin, adultId: adultId || getDeviceOwnerAdultId(code) || undefined });
  } catch {
    return false;
  }
}
function deviceTokenHeaders(code) {
  const t = getDeviceToken(code);
  return t ? { "X-Device-Token": t } : {};
}

// LB-179 — the ONE way every household route other than the state GET/POST is called.
// Attaches X-Device-Token (when we hold one) and X-Device-Id, picks up any token the
// worker issues, and mirrors apiGet's 401 posture: a rejected token is discarded and the
// call retried ONCE without it (tolerated while the dual-accept window is open); a second
// 401 is the "rejoin" signal. Before this helper only apiGet/apiSet/apiMintDeviceToken
// sent the token, so the worker's gate on the other 14 routes would have 401'd real
// devices the moment it landed — the 29 Aug incident class. Adding a route? Call this,
// not fetch(). The pre-token routes (device-token, verify-passcode, verify-jointoken,
// the recovery/reset pairs, welcome-email, resend-verification) may use it too — the
// worker simply doesn't gate them — so there is no reason to reach for bare fetch().
export async function householdFetch(code, path, init = {}, _isRetry = false) {
  const headers = {
    ...deviceTokenHeaders(code),
    "X-Device-Id": getDeviceId(),
    ...(init.headers || {}),
    ...(_isRetry ? { "X-Device-Token-Retry": "1" } : {}),
  };
  const res = await fetch(apiUrl(`${API_BASE}/${code}${path}`), { ...init, headers });
  pickUpIssuedToken(code, res);
  if (res.status === 401) {
    let data = null;
    try { data = await res.clone().json(); } catch {}
    if (data && data.tokenRequired) {
      clearDeviceToken(code);
      if (!_isRetry) return householdFetch(code, path, init, true);
      const err = new Error("This device needs to rejoin the household");
      err.revoked = true;
      throw err;
    }
  }
  return res;
}
function pickUpIssuedToken(code, res) {
  try {
    const issued = res.headers.get("X-Device-Token-Issue");
    if (issued) setDeviceToken(code, issued);
    const proof = res.headers.get("X-Device-Proof"); // LB-178: the worker's word beats ours
    if (proof) setDeviceProof(code, proof);
  } catch {}
}

// Mints this device's token with proof of belonging — called after a successful
// create (passcode from the fresh state) or join (whichever credential just worked).
// Failure is non-fatal during the dual-accept window: the next GET auto-mints anyway.
// Same-device handover (Sep 2026). Asks the worker for a single-use, 24-hour ticket that the
// installed app's first launch can present instead of the passcode. Only a device that already
// holds a token gets one — the route runs requireDevice — so a fresh or revoked device is
// refused with the same 401 it gets everywhere else. Returns the ticket or null; null is
// non-fatal, the install just falls back to carrying the code alone as it did before.
export async function apiIssueHandover(code) {
  try {
    // householdFetch prefixes API_BASE and the code itself — pass only the suffix. The first
    // cut passed the full path and every request went to /api/household/X/api/household/X/
    // handover, a 404, which fell back to the code-only carry so quietly that Alan's staging
    // test read as "nothing happened". Found from the URL he sent: ?resume=CODE with no &t=.
    const res = await householdFetch(code, "/handover", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    return res.ok && typeof data.ticket === "string" ? data.ticket : null;
  } catch {
    return null;
  }
}

// Redeem a handover ticket. Returns { ok, retry }: `retry` means the worker said the ticket
// was not found, which within the first seconds after install is almost always KV lag rather
// than a dead ticket, so Root tries again with backoff before falling to the join screen.
export async function apiRedeemHandover(code, ticket) {
  try {
    const res = await fetch(apiUrl(`${API_BASE}/${code}/device-token`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: getDeviceId(), handoverTicket: ticket }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.token) {
      setDeviceToken(code, data.token);
      setDeviceProof(code, data.proof || "handover");
      return { ok: true, retry: false };
    }
    return { ok: false, retry: !!data.handoverMiss };
  } catch {
    return { ok: false, retry: true }; // network blip: worth another go
  }
}

// ---- LB-341: Add a device on one-time tickets (F1, F2, F3) ----
//
// Five calls, one state machine. The joining device never holds anything that mints on its
// own — it presents a short code, that parks a request, and the issuing adult approves it.
// Read worker/lib/joinTickets.js for why the code can be eight characters.

// F1/F2 — the issuing adult asks for a code. `forOwnDevice` is F1's "This is for" answer and
// decides whether the minted device records this adult as its owner.
// LB-370 — a household's own referral link. GET reads the summary without minting (the
// Settings row's "N friends have joined" line); POST mints the standing code on first use
// and hands the same one back every time after. Shape: { ok, minted, inviteCode, uses,
// maxUses, usesLeft, disabled, joinedCount }. Same refusal-is-not-a-connection-problem
// posture as apiIssueJoinTicket below.
export async function apiGetReferral(code) {
  try {
    const res = await householdFetch(code, "/referral");
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) return { ok: true, ...data };
    return { ok: false, status: res.status, error: data.error || null, networkError: false };
  } catch {
    return { ok: false, status: 0, error: null, networkError: true };
  }
}

export async function apiMintReferral(code) {
  try {
    const res = await householdFetch(code, "/referral", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok && typeof data.inviteCode === "string") return { ok: true, ...data };
    return { ok: false, status: res.status, error: data.error || null, networkError: false };
  } catch {
    return { ok: false, status: 0, error: null, networkError: true };
  }
}

export async function apiIssueJoinTicket(code, { forOwnDevice = false } = {}) {
  try {
    const res = await householdFetch(code, "/join-ticket", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ forOwnDevice: !!forOwnDevice }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && typeof data.ticket === "string") return { ok: true, ticket: data.ticket, display: data.display, expiresAt: data.expiresAt };
    // A REFUSAL IS NOT A CONNECTION PROBLEM. The first cut returned null for every non-ok
    // response and the caller said "check your connection" — sending a parent to their router
    // while the server was explaining, in words, that the device was not recognised.
    // Principle 5: say what actually happened. networkError is the ONLY case that is really
    // about the connection, and it is the only one that says so.
    return { ok: false, status: res.status, error: data.error || null, networkError: false };
  } catch {
    return { ok: false, status: 0, error: null, networkError: true };
  }
}

// The joining device asks to join. Bare fetch on purpose, not householdFetch: this device has
// no token yet, which is the whole reason the route exists. Returns { ok, requestId, error }.
export async function apiRequestJoin(code, ticket) {
  try {
    const res = await fetch(apiUrl(`${API_BASE}/${code}/join-request`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: getDeviceId(), ticket }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && !!data.requestId, requestId: data.requestId || null, error: data.error || null };
  } catch {
    return { ok: false, requestId: null, error: null };
  }
}

// Polled by the joining device while it waits. "gone" means cancelled or expired — a real
// answer, not an error, and the screen says so rather than spinning for ever.
export async function apiJoinRequestStatus(code, requestId) {
  try {
    const res = await fetch(apiUrl(`${API_BASE}/${code}/join-request/${encodeURIComponent(requestId)}`));
    const data = await res.json().catch(() => ({}));
    return res.ok && typeof data.status === "string" ? data.status : "pending";
  } catch {
    return "pending"; // a blip is not a refusal; keep waiting
  }
}

// F3 — what this adult has been asked to approve. The worker filters to theirs.
// null means "the call failed", which is NOT the same as "there are no requests" — the caller
// backs off on null and would otherwise retry a refusal for ever.
export async function apiGetJoinRequests(code) {
  try {
    const res = await householdFetch(code, "/join-requests");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    return Array.isArray(data.requests) ? data.requests : [];
  } catch {
    return null;
  }
}

// F3's two buttons. `ticket` is sent on cancel so the code dies with the request.
export async function apiActOnJoinRequest(code, requestId, action, ticket = null) {
  try {
    const res = await householdFetch(code, `/join-requests/${encodeURIComponent(requestId)}/${action}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ticket ? { ticket } : {}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Once approved, the joining device mints with the same code. The worker checks the APPROVAL,
// not the code, and that the deviceId is the one that asked.
export async function apiRedeemJoinTicket(code, ticket) {
  try {
    const res = await fetch(apiUrl(`${API_BASE}/${code}/device-token`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId: getDeviceId(), joinTicket: ticket }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.token) {
      setDeviceToken(code, data.token);
      setDeviceProof(code, data.proof || "handover");
      setDeviceOwnerAdultId(code, data.ownerAdultId || null);
      // F1's answer, carried back so the arriving device can set its own role rather than
      // asking a question the issuing adult has already answered. A ticket with no owning
      // adult is a shared device, which is also the safe reading of a missing field.
      return { ok: true, waiting: false, ownerAdultId: data.ownerAdultId || null, forOwnDevice: data.forOwnDevice === true };
    }
    // 429 is the limiter, which is TEMPORARY — reporting it as terminal is how a device
    // that had just been approved was told its code had run out. Anything else without
    // `joinPending` really is terminal: an unknown, expired or already-spent ticket.
    // The reason travels with the refusal. Two rounds of this defect were spent on a screen
    // that showed its own copy for every terminal path — "That code has run out" for a
    // revoked device, for a wrong code, for anything — so the report never carried what the
    // server actually said (Alan, staging, 21-22 Sep).
    return { ok: false, waiting: !!data.joinPending || res.status === 429, error: typeof data.error === "string" ? data.error : null };
  } catch {
    return { ok: false, waiting: true };
  }
}

// ---- LB-342: invite an adult, accept by link ----
//
// The two halves go through different doors on purpose. Inviting rides householdFetch, so it
// carries this device's token; peek and accept are bare fetches, because the person holding
// the link has no device in this household yet — which is the whole point of the link.

export async function apiInviteAdult(code, { name, email }) {
  try {
    const res = await householdFetch(code, "/adult-invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, error: data.error || null, invite: data.invite || null, invites: Array.isArray(data.invites) ? data.invites : null, full: !!data.full, duplicate: !!data.duplicate, notAllowed: !!data.notAllowed };
  } catch {
    return { ok: false, error: null };
  }
}

// LB-312 — the grantable permission. Its own route because adults are server-owned on save:
// the save path's merge carries a name and a proven PIN across from a client's copy and
// nothing else, so update() could never have persisted this. The response is the whole
// adults list, pinless, which the caller REPLACES its copy with.
export async function apiSetAdultPermission(code, adultId, permission, value) {
  try {
    const res = await householdFetch(code, `/adults/${encodeURIComponent(adultId)}/permissions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ permission, value: !!value }),
    });
    const data = await res.json().catch(() => ({}));
    return {
      ok: res.ok,
      adults: Array.isArray(data.adults) ? data.adults : null,
      ownerOnly: !!data.ownerOnly,
      error: data.error || null,
    };
  } catch {
    return { ok: false, adults: null, ownerOnly: false, error: null };
  }
}

export async function apiInviteAction(code, inviteId, action) {
  try {
    const res = await householdFetch(code, `/adult-invite/${encodeURIComponent(inviteId)}/${action}`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    // `invites` is the server's whole list after the action. The caller REPLACES its copy
    // with it rather than editing its own — see apiInviteAdult's caller for why.
    return { ok: res.ok, invites: Array.isArray(data.invites) ? data.invites : null };
  } catch {
    return { ok: false, invites: null };
  }
}

// B2 — what the invited device may see before committing to anything. Bare fetch: no token.
export async function apiPeekInvite(code, token) {
  try {
    const res = await fetch(apiUrl(`${API_BASE}/${code}/adult-invite/peek`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await res.json().catch(() => ({}));
    return res.ok ? data : null;
  } catch {
    return null;
  }
}

// B3/B4 — accept. Mints this device's token, recorded as proof kind "invite".
export async function apiAcceptInvite(code, token, { pin, ownDevice }) {
  try {
    const res = await fetch(apiUrl(`${API_BASE}/${code}/adult-invite/accept`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, deviceId: getDeviceId(), pin, ownDevice: ownDevice !== false }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.token) {
      setDeviceToken(code, data.token);
      setDeviceProof(code, data.proof || "invite");
      setDeviceOwnerAdultId(code, data.ownerAdultId || null);
      // The adult who just accepted IS the one holding this device, so Parent Mode this
      // session is theirs without a second PIN prompt.
      setActiveAdultId(code, data.adultId || null);
      return { ok: true, adultId: data.adultId || null };
    }
    return { ok: false, error: data.error || null, gone: !!data.gone };
  } catch {
    return { ok: false, error: null };
  }
}

// ---- LB-344: remove an adult, or leave (E2, E3) ----
//
// One call for both. Leaving is removing yourself, and giving them separate endpoints would
// be giving them separate teardowns to drift apart.
export async function apiRemoveAdult(code, adultId) {
  try {
    const res = await householdFetch(code, `/adults/${encodeURIComponent(adultId)}/remove`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, error: data.error || null, ownerProtected: !!data.ownerProtected };
  } catch {
    return { ok: false, error: null };
  }
}

// E2's inline row, and deliberately its own call: a cancel of the removal must not rotate the
// passcode, and a removal must not force it.
export async function apiRotatePasscode(code) {
  try {
    const res = await householdFetch(code, "/rotate-passcode", { method: "POST" });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, passcode: data.passcode || null, error: data.error || null };
  } catch {
    return { ok: false, passcode: null, error: null };
  }
}

export async function apiMintDeviceToken(code, proof) {
  const res = await fetch(apiUrl(`${API_BASE}/${code}/device-token`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId: getDeviceId(), ...proof }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data.token) {
    setDeviceToken(code, data.token);
    setDeviceProof(code, data.proof || "auto"); // LB-178
    // LB-340: null on a passcode/QR join, which is correct — that is a shared device, and
    // setDeviceOwnerAdultId(null) clears any stale claim from a previous household member.
    setDeviceOwnerAdultId(code, data.ownerAdultId || null);
  }
  return res.ok;
}

export async function apiGet(code, _isRetry = false) {
  const res = await fetch(apiUrl(`${API_BASE}/${code}?deviceId=${encodeURIComponent(getDeviceId())}`), {
    headers: { ...deviceTokenHeaders(code), ...(_isRetry ? { "X-Device-Token-Retry": "1" } : {}) },
  });
  pickUpIssuedToken(code, res);
  const data = await res.json();
  // HOTFIX 29 Aug: a token 401 gets ONE token-less retry before this device tears itself
  // down. The mint path is a racy read-modify-write (two near-simultaneous first-contact
  // requests both mint; last hash written wins; the client may hold the other token), so
  // a single mismatch is weak evidence of anything — while the client's response to it
  // was maximal: wipe the code, cache and token, and bounce to onboarding. That
  // asymmetry is what turned one bad deploy-time race into a fleet-wide kickout. Under
  // dual-accept the token-less retry always succeeds and a fresh token is issued in the
  // response; under enforcement the retry 401s too and the teardown below still runs,
  // exactly as designed.
  if (res.status === 401 && data && data.tokenRequired) {
    clearDeviceToken(code);
    if (!_isRetry) return apiGet(code, true);
    const err = new Error("This device needs to rejoin the household");
    err.revoked = true;
    throw err;
  }
  throwIfGone(res, data);
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  if (data.revoked) {
    // This device has been removed from the household by a parent on another device (see
    // Settings → Devices). Root.jsx catches err.revoked specifically and bounces this
    // device back to onboarding rather than showing a generic error.
    const err = new Error("This device was removed from the household");
    err.revoked = true;
    throw err;
  }
  return data.value;
}

// Shared "this household has been deleted" shape (28 Aug, staging test G). The worker
// keeps a `deleted:{code}` tombstone forever, and answers both reads and writes for a
// retired code with `gone: true` — a read as 200 (so it isn't swallowed by the generic
// !res.ok branch below), a write as 410. Deliberately its OWN flag rather than reusing
// err.revoked: revocation means "this device was removed, rejoin to get back in", and
// offering a rejoin for a household that no longer exists would send someone hunting for
// a passcode that can never work again. Root.jsx unlinks and explains instead.
function throwIfGone(res, data) {
  if (!data || !data.gone) return;
  const err = new Error("This household has been deleted");
  err.gone = true;
  throw err;
}

// (throwIfTokenRequired removed 29 Aug: both its call sites now do a one-retry-then-
// teardown inline. The single-401 immediate teardown it encoded is what amplified the
// deploy-time mint race into a fleet-wide kickout — do not reintroduce it.)

// opts (LB-184): { creationTicket } on the save that CREATES a household — Root.jsx's
// handleStart / handleRestore pass the ticket /api/invite/validate issued; every other save
// omits it. An options object rather than a sixth positional argument: there are already five.
// _isRetry rides in the same object (it was the sixth positional before).
export async function apiSet(code, obj, verifiedPin, verifiedPasscode, verifiedJoinToken, opts = {}) {
  const { creationTicket = null, _isRetry = false } = opts || {};
  const res = await fetch(apiUrl(`${API_BASE}/${code}`), {
    method: "POST",
    headers: { ...deviceTokenHeaders(code), ...(_isRetry ? { "X-Device-Token-Retry": "1" } : {}) },
    body: JSON.stringify({ state: obj, verifiedPin: verifiedPin || null, verifiedPasscode: verifiedPasscode || null, verifiedJoinToken: verifiedJoinToken || null, deviceId: getDeviceId(), ...(creationTicket ? { creationTicket } : {}) }),
  });
  pickUpIssuedToken(code, res);
  const data = await res.json();
  if (res.status === 409 && data && data.conflict) {
    // Save-concurrency gate (docs/SAVE_CONCURRENCY_SCOPE.md): the server holds a NEWER
    // revision than the one this save was built on, and refused to let ours overwrite
    // it. The current server copy rides in the body so Root.jsx can rebase pending
    // local ops onto it and retry — the invisible merge. Distinct error shape on
    // purpose: callers must never mistake this for a network failure (it must not trip
    // the offline machinery) or a permission failure.
    const err = new Error("Another device saved a newer copy of this household");
    err.conflict = true;
    err.revision = data.revision;
    err.serverValue = data.value || null;
    throw err;
  }
  // Same one-retry-before-teardown rule as apiGet above, same reasoning: a single token
  // mismatch is a race artefact more often than a removal, and a save must not cost a
  // family their device link over it.
  if (res.status === 401 && data && data.tokenRequired) {
    clearDeviceToken(code);
    if (!_isRetry) return apiSet(code, obj, verifiedPin, verifiedPasscode, verifiedJoinToken, { ...(opts || {}), _isRetry: true });
    const err = new Error("This device needs to rejoin the household");
    err.revoked = true;
    throw err;
  }
  throwIfGone(res, data);
  // LB-184: the worker will not CREATE a household without a live creation ticket. Tagged so
  // Root.jsx can send the person back to the invitation gate rather than show a raw error.
  if (res.status === 403 && data && data.inviteRequired) {
    const err = new Error(data.error || "Invitation needed");
    err.inviteRequired = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  if (data.revoked) {
    const err = new Error("This device was removed from the household");
    err.revoked = true;
    throw err;
  }
  return true;
}


// `adultId` is optional: omitted, the device's own adult is used, and if it has none the
// worker reads that as the Owner. Pass one explicitly for "Log in as {name}".
export async function apiVerifyPin(code, pin, adultId) {
  const res = await householdFetch(code, `/verify-pin`, {
    method: "POST",
    body: JSON.stringify({ pin, adultId: adultId || getDeviceOwnerAdultId(code) || undefined }),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    // LB-177: the worker now locks the PIN for 15 minutes after 10 wrong guesses and says
    // so in its message. Flag it so the pad shows THAT rather than the generic
    // "check your connection" wording, which would send a parent chasing their wifi.
    if (res.status === 429) { err.rateLimited = true; err.retryAfterSeconds = data.retryAfterSeconds || null; }
    throw err;
  }
  // LB-312 part 2 fix: return WHO was proved, not just whether. The worker resolves an absent
  // adultId to the Owner and replies with the adult it actually checked — and throwing that
  // away is why a device minted before part 1 (no stored ownerAdultId) had no adult on the
  // session at all: Parent Mode unlocked, but every surface that asks "who is this?" saw
  // nobody, so the Adults card hid its Invite row.
  return { valid: !!data.valid, adultId: data.adultId || null };
}


// Checks a household code + passcode pair before a new device is allowed to join. Never
// returns the real passcode — true/false only. Rate-limited server-side per household
// code; a 429 is surfaced to the caller as a distinct `rateLimited` error so the Join
// screen can show "try again in a few minutes" instead of the generic mismatch message.
export async function apiVerifyPasscode(code, passcode) {
  const res = await householdFetch(code, `/verify-passcode`, {
    method: "POST",
    body: JSON.stringify({ passcode }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many attempts — try again in a few minutes");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return !!data.valid;
}

// The only way a client ever learns the real household passcode — requires the PIN this
// device already unlocked Parent Mode with. Used by Settings to show/copy the passcode
// and to prove the current value when changing it.
export async function apiRevealPasscode(code, verifiedPin) {
  const res = await householdFetch(code, `/reveal-passcode`, {
    method: "POST",
    body: JSON.stringify({ verifiedPin, adultId: getDeviceOwnerAdultId(code) || undefined }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data.passcode || null;
}

// The only way a client ever learns the real quick-join token — same PIN-gate shape as
// apiRevealPasscode above. Used by Settings to render the QR (and to prove the current
// value if the parent taps Regenerate).
export async function apiRevealJoinToken(code, verifiedPin) {
  const res = await householdFetch(code, `/reveal-jointoken`, {
    method: "POST",
    body: JSON.stringify({ verifiedPin, adultId: getDeviceOwnerAdultId(code) || undefined }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data.joinToken || null;
}

// Checks a household code + quick-join token pair before a device scanning a QR is let
// in — the token-based sibling of apiVerifyPasscode above, same rate-limited shape.
export async function apiVerifyJoinToken(code, token) {
  const res = await householdFetch(code, `/verify-jointoken`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many attempts — try again in a few minutes");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return !!data.valid;
}

// Step 1 of passcode recovery: request an email with a reset link, sent only to the
// address on file as this household's owner. Always resolves true — the server responds
// { ok: true } whether or not the email actually matched, so the UI can never learn
// which. A 429 here means too many recovery requests for this household recently.
export async function apiRequestPasscodeRecovery(code, email) {
  const res = await householdFetch(code, `/request-recovery`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many requests — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

// Step 2 of passcode recovery: redeem the emailed token for a new passcode. The token is
// single-use and expires after 30 minutes — a 403 here means the link is stale or was
// already used, and the person needs to request a fresh one.
export async function apiResetPasscode(code, token, newPasscode) {
  const res = await householdFetch(code, `/reset-passcode`, {
    method: "POST",
    body: JSON.stringify({ token, newPasscode }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many attempts — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

// Email-ownership pre-create check (review 2.2): "does this email already run a
// household?" Fails OPEN by design — see Onboarding.jsx's create-tab call site, which
// treats a thrown error here exactly like exists:false and proceeds with creation. A
// 429 here means too many checks from this connection recently.
export async function apiOwnerCheck(email) {
  const res = await fetch(apiUrl(`/api/owner-check`), {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many checks — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return !!data.exists;
}

// Fork card's "Email me my household code(s)" button. Always resolves true — the server
// responds { ok: true } whether or not the index actually had entries for this email.
export async function apiOwnerRecap(email) {
  const res = await fetch(apiUrl(`/api/owner-recap`), {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return true;
}

// Server-side invite-code check (review 1.1b/P4) — replaces the old client-side
// input === INVITE_CODE comparison. A 403 here means the code is wrong, expired, or
// already used up; a 429 means too many attempts from this connection. Resolves to the
// creation ticket string (LB-184), or null if the server sent none.
export async function apiValidateInvite(inviteCode) {
  const res = await fetch(apiUrl(`/api/invite/validate`), {
    method: "POST",
    body: JSON.stringify({ code: inviteCode }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many attempts — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) {
    const err = new Error(data.error || "That invitation code isn't right — check it and try again.");
    throw err;
  }
  // LB-184: the creation ticket (worker/lib/creationTicket.js). Null when the response has
  // none — the Playwright suite's mocked route answers { ok: true } alone — so the pass is
  // still granted and only the real worker's creation gate would notice.
  return typeof data.ticket === "string" && data.ticket ? data.ticket : null;
}

// Settings → Household's "Resend confirmation" link, for a household whose owner email
// hasn't been clicked-to-verify yet. A 429 means too many resends recently for this code.
// W2-PR(iii) part 2: owner email change goes through a confirm loop — this only ASKS;
// the change happens when the link sent to the NEW address is clicked (see
// worker/handlers/email-change.js).
export async function apiRequestEmailChange(code, newEmail, verifiedPin) {
  const res = await householdFetch(code, `/request-email-change`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newEmail, verifiedPin, adultId: getDeviceOwnerAdultId(code) || undefined }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

export async function apiResendVerification(code) {
  const res = await householdFetch(code, `/resend-verification`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many resend requests — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

// Step 1 of PIN recovery: request an email with a reset link, sent only to the address
// on file as this household's owner. Always resolves true — same uniform-response
// shape as apiRequestPasscodeRecovery, and for the same reason.
export async function apiRequestPinRecovery(code, email) {
  const res = await householdFetch(code, `/request-pin-recovery`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many requests — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

// Step 2 of PIN recovery: redeem the emailed token for a new Parent PIN. The token is
// single-use and expires after 30 minutes.
export async function apiResetPin(code, token, newPin) {
  const res = await householdFetch(code, `/reset-pin`, {
    method: "POST",
    body: JSON.stringify({ token, newPin }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    const err = new Error(data.error || "Too many attempts — try again later");
    err.rateLimited = true;
    throw err;
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

// Devices (Parent Mode → Settings → Devices). Fetched on demand, only while that card is
// open — not baked into the ordinary household GET. See worker/lib/devices.js for the
// stored shape: { [deviceId]: { label, customLabel, firstSeenAt, lastSeenAt, revoked } }.
export async function apiGetDevices(code) {
  const res = await householdFetch(code, `/devices`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data.devices || {};
}

export async function apiRenameDevice(code, deviceId, label) {
  const res = await householdFetch(code, `/devices/${encodeURIComponent(deviceId)}/rename`, {
    method: "POST",
    body: JSON.stringify({ label }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}

// Kicks a device out — see worker/lib/devices.js. The removed device notices on its own
// next request (GET or POST, whichever it does first) via the `revoked` handling in
// apiGet/apiSet above, typically within seconds given the app's existing foreground/focus
// refetch behaviour.
// LB-178: a parent vouches for a device from Settings → Devices (PIN-proved server-side).
export async function apiConfirmDevice(code, deviceId, verifiedPin) {
  const res = await householdFetch(code, `/devices/${encodeURIComponent(deviceId)}/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ verifiedPin, adultId: getDeviceOwnerAdultId(code) || undefined }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data.proof || "parent";
}

export async function apiRemoveDevice(code, deviceId) {
  const res = await householdFetch(code, `/devices/${encodeURIComponent(deviceId)}/remove`, {
    method: "POST",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return true;
}


export async function apiGetVapidKey() {
  const res = await fetch(apiUrl("/api/push/vapid-public-key"));
  const data = await res.json();
  return data.key || null;
}

export async function apiPushSubscribe(code, role, childId, subscription) {
  const res = await householdFetch(code, `/push/subscribe`, {
    method: "POST",
    // deviceId lets Settings → Devices report which devices will actually be alerted. Sent
    // from here rather than derived server-side because the device id lives in localStorage
    // and is already the identity every other per-device feature uses.
    body: JSON.stringify({ role, childId, deviceId: getDeviceId(), subscription: subscription.toJSON ? subscription.toJSON() : subscription }),
  });
  return res.ok;
}

// Household-wide notification coverage — counts only, no endpoints. Answers the question no
// single device can answer for itself: "is anyone in this household actually going to be told
// when a request comes in?"
export async function apiGetPushStatus(code) {
  try {
    const res = await householdFetch(code, `/push/status`);
    if (!res.ok) return null;
    const data = await res.json();
    return { parents: data.parents || 0, children: data.children || {} };
  } catch {
    return null;
  }
}

// Fires one real push at this device's own endpoint, through exactly the same VAPID signing
// and web-push path every genuine notification uses. That's the point: a green toggle only
// proves permission was granted, not that anything will ever actually arrive.
export async function apiPushTest(code, endpoint) {
  const res = await householdFetch(code, `/push/test`, {
    method: "POST",
    body: JSON.stringify({ endpoint }),
  });
  return res.ok;
}

export async function apiPushUnsubscribe(code, endpoint) {
  const res = await householdFetch(code, `/push/unsubscribe`, {
    method: "POST",
    body: JSON.stringify({ endpoint }),
  });
  return res.ok;
}

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// Word bank submission (TTS_SCOPE.md §5.3.1). Deliberately its own endpoint, NOT
// ${API_BASE}/${code}/... — a household code appearing anywhere in this request would
// reintroduce the exact word<->household link the whole design exists to avoid. Never
// takes a code, never reads one, never sends one.
//
// Fire-and-forget by design: the caller (App.jsx's flush effect) doesn't need to
// know whether this succeeded. A dropped submission just means that word waits for the
// next flush; nothing about the app's own behaviour depends on the outcome.
// The server takes at most WORDBANK_BATCH words per request (worker/handlers/wordbank.js) and
// silently drops the rest, and it allows 10 flushes an hour per IP. The queue used to go up in
// ONE request and then be cleared whole, so anything past the 20th word was lost for good; a
// homework spelling list alone can be 60 words (Alan, 29 Sep: "Homework words aren't being
// queued for audio generation"). So a flush sends up to WORDBANK_FLUSH_BATCHES requests of
// WORDBANK_BATCH words, and whatever is left over stays queued for the next open. Duplicates
// in the queue are sent once.
export const WORDBANK_BATCH = 20;
export const WORDBANK_FLUSH_DELAY_MS = 4000; // after the queue last changed; gathers a burst of adds
export const WORDBANK_FLUSH_BATCHES = 3;
export function wordbankFlushPlan(pending) {
  const seen = new Set(); const unique = [];
  for (const w of pending || []) {
    const t = typeof w === "string" ? w.trim() : "";
    if (!t || seen.has(t)) continue;
    seen.add(t); unique.push(t);
  }
  const max = WORDBANK_BATCH * WORDBANK_FLUSH_BATCHES;
  const send = [];
  for (let i = 0; i < Math.min(unique.length, max); i += WORDBANK_BATCH) send.push(unique.slice(i, i + WORDBANK_BATCH));
  return { batches: send, keep: unique.slice(max) };
}

export async function apiSubmitWordbank(words) {
  try {
    await fetch(apiUrl("/api/wordbank"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ words }),
    });
  } catch {
    // Offline, or the request otherwise failed — the words stay queued in household
    // state (see App.jsx) and this is simply tried again on the next app open.
  }
}
